import { useState, useEffect, useCallback } from 'react';
import { adminAPI, APIError } from '../lib/api';
import styles from './Waitlist.module.css';

interface WaitlistSignup {
  id: string;
  email: string;
  created_at: string;
}

type SortField = 'email' | 'created_at';
type SortOrder = 'asc' | 'desc';

const PAGE_SIZE = 25;

export function Waitlist() {
  const [signups, setSignups] = useState<WaitlistSignup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<SortField>('created_at');
  const [order, setOrder] = useState<SortOrder>('desc');
  const [exporting, setExporting] = useState(false);

  const fetchSignups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminAPI.getWaitlist({
        sort,
        order,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setSignups(data.signups);
      setTotal(data.total);
    } catch (err) {
      if (err instanceof APIError) {
        setError(err.message);
      } else {
        setError('Failed to load waitlist');
      }
    } finally {
      setLoading(false);
    }
  }, [sort, order, page]);

  useEffect(() => {
    fetchSignups();
  }, [fetchSignups]);

  const handleSort = (field: SortField) => {
    if (sort === field) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(field);
      setOrder(field === 'created_at' ? 'desc' : 'asc');
    }
    setPage(0);
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      // Fetch all signups for export
      const data = await adminAPI.getWaitlist({
        sort: 'created_at',
        order: 'asc',
        limit: 10000,
        offset: 0,
      });

      const header = 'Email,Signup Date\n';
      const rows = data.signups
        .map((s) => `"${s.email}","${new Date(s.created_at).toISOString()}"`)
        .join('\n');

      const blob = new Blob([header + rows], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const sortIndicator = (field: SortField) =>
    sort === field ? (order === 'asc' ? ' ↑' : ' ↓') : '';

  if (error && !signups.length) {
    return <div className={styles.error}>{error}</div>;
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>Waitlist</h1>
          {!loading && (
            <span className={styles.count}>
              {total.toLocaleString()} signup{total !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <button
          className={styles.exportButton}
          onClick={handleExportCSV}
          disabled={exporting || total === 0}
        >
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </header>

      {loading && !signups.length ? (
        <div className={styles.loading}>Loading waitlist…</div>
      ) : signups.length === 0 ? (
        <div className={styles.empty}>No signups yet.</div>
      ) : (
        <>
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th
                  className={styles.sortable}
                  onClick={() => handleSort('email')}
                >
                  Email{sortIndicator('email')}
                </th>
                <th
                  className={styles.sortable}
                  onClick={() => handleSort('created_at')}
                >
                  Signed Up{sortIndicator('created_at')}
                </th>
              </tr>
            </thead>
            <tbody>
              {signups.map((signup) => (
                <tr key={signup.id} className={styles.tableRow}>
                  <td className={styles.email}>{signup.email}</td>
                  <td className={styles.date}>
                    {new Date(signup.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <span className={styles.paginationInfo}>
                Page {page + 1} of {totalPages}
              </span>
              <div className={styles.paginationButtons}>
                <button
                  className={styles.paginationButton}
                  disabled={page === 0}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </button>
                <button
                  className={styles.paginationButton}
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
